<template>
  <div class="locations-discount mt-70">
    <div class="locations-discount-info">
      <span v-html="mainText" />
      <div class="locations-discount-items mt-30">
        <locations-discount-item 
          v-for="item, index in items" 
          :key="index"
          :item="item" 
        />
      </div>
      <template v-if="isOneLocation || isRegionPage">
        <div v-html="descriptionText" class="public-text-line my-30" />
        <locations-tariffs v-if="!isRegionPage" class="my-70"/>
        <locations-discount-terminals 
          v-if="isOneLocation && !!fromTerminals.length" 
          :terminals="fromTerminals" 
        />
      </template>
    </div>
    <locations-discount-banner />
    <div v-if="!isOneLocation && !isRegionPage" v-html="descriptionText" class="locations-discount-description public-text-line" />
</div>
</template>

<script setup lang="ts">
import {PropType} from 'vue'
import {storeToRefs} from 'pinia'
import {useCityStore} from '@/stores/city'
import {UrlDirection, LocationRegion} from '~/types/types'
import LocationsTariffs from '@/components/public/locations/locations-tariffs.vue'
import LocationsDiscountItem from '@/components/public/locations/locations-discount/locations-discount-item.vue'
import LocationsDiscountBanner from '@/components/public/locations/locations-discount/locations-discount-banner.vue'
import LocationsDiscountTerminals from '@/components/public/locations/locations-discount/locations-discount-terminals.vue'
import {PublicLocationsTag} from '~/types/public'

const props = defineProps({
  isOneLocation: {
    type: Boolean,
    default: false
  },
  region: {
    type: Object as PropType<LocationRegion>,
    default: () => {}
  },
  refCalculator: {
    type: Object,
    default: null
  },
  urlDirection: {
    type: Object as PropType<UrlDirection>,
    default: null
  },
  tags: {
    type: Array as PropType<PublicLocationsTag[]|Location[]>,
    default: () => []
  },
})

const { from, to } = storeToRefs(useCityStore())
const { terminalList } = storeToRefs(useTerminalStore())

const isRegionPage = computed(() => {
  return !!props.region?.id
})

const locationText = computed(() => {
  return isRegionPage.value
    ? props.region?.dative || `${props.region?.name} ${props.region?.type}` 
    : props.isOneLocation
      ? `${from.value.type}. ${from.value.name}`
      : `${from.value.name} — ${to.value.name}`
})

const mainText = computed(() => {
  return isRegionPage.value
    ? `Транспортная компания Vozovoz осуществляет грузоперевозки по ${locationText.value} и за ее 
      пределами. У нас вы можете недорого заказать доставку груза по всей России автомобильным 
      транспортом.`
    : props.isOneLocation
      ? `Транспортная компания "Возовоз" обеспечивает услуги перевозки груза из/в 
        ${from.value.prepositional_name3 || locationText.value} по России${tagsCountText.value}. 
        Наша специализация - грузоперевозки сборных грузов, что дает возможность клиентам снизить 
        стоимость и сроки транспортировки. ${fromTerminalDescriptionText.value}`
      : `Грузоперевозки ${locationText.value} осуществляются автомобильным транспортом несколько 
        раз в неделю. Мы принимаем груз от физических лиц и компаний.`
})

const fromTerminals = computed(() => {
  return terminalList.value.filter(t => t.location.guid === from.value.guid)
})

const fromTerminalDescriptionText = computed(() => {
  return props.isOneLocation && fromTerminals.value?.length && fromTerminals.value[0].description?.length
    ? `<br/> На терминале города ${from.value.prepositional_name2 || from.value.name} действуют ограничения по грузу: ${fromTerminals.value[0].description}.`
    : ''
})

const itemsTerminalsText = computed(() => {
  const toTerminalsCount = terminalList.value.filter(t => t.location.guid === to.value.guid)?.length || 0
  const fromTerminalsCount = fromTerminals.value?.length || 0

  return fromTerminalsCount > 0 && toTerminalsCount > 0
    ? ` - ${fromTerminalsCount} ${getTerminalTextEnd(fromTerminalsCount)} в ${from.value.type}. ${from.value.name} и 
      ${toTerminalsCount} ${getTerminalTextEnd(toTerminalsCount)} в ${to.value.type}. ${to.value.name}`
    : ''
})

const tagsCountText = computed(() => {
  let text = ''
  if (props.isOneLocation && props.tags && props.tags?.length) { 
    text = props.tags?.length === 1 
      ? ' и в 1 населенный пункт' 
      : ` и в ${props.tags.length} населенных пукта`
  }

  return text
})

const descriptionText = computed(() => {
  return isRegionPage.value
    ? `Рассчитайте стоимость перевозки по ${locationText.value} в калькуляторе с учетом параметров 
      груза (веса, длины, ширины, высоты), расстояния между пунктами отправления и назначения и 
      выбранных дополнительных услуг - упаковки, необходимости погрузки-разгрузки и страхования`
    : props.isOneLocation
      ? `Для расчета стоимости и сроков перевозки в ${locationText.value} 
        введите параметры груза в онлайн-калькулятор`
      : `Рассчитайте стоимость перевозки по маршруту с учетом упаковки груза,<br> его габаритов, 
        необходимости погрузки-разгрузки и страхования в онлайн-калькуляторе`
})

const items = computed(() => {
  const data = {
    imgUrl: '/svg/vz-coins.svg',
    title: 'Стоимость перевозки',
    text: [
      isRegionPage.value
        ? `Доставка "от двери до двери" по ${locationText.value} - ${props.refCalculator?.priceData?.total || ''} ₽.`
        : props.isOneLocation 
          ? `Цена транспортировки зависит от параметров груза (веса, длины, ширины, высоты), 
            расстояния между пунктами отправления и назначения, выбранных дополнительных 
            услуг.
            <ul class="public-list public-list-square">
              <li><span>
                Заказать доставку сборного груза 
                ${props.refCalculator?.priceData?.total ? ('от ' + props.refCalculator?.priceData?.total + ' ₽') : ''}
              </span></li>
              <li><span>
                <a href="#locations-popular-directions" class="color-dark text-decoration-underline">
                  Цены на грузоперевозки на популярных направлениях
                </a>
              </span></li>
            </ul>`
          : `Цена за услугу "Перевозка между городами" на маршруте ${locationText.value} от 
            ${+props.urlDirection?.price?.price || ''} ₽.`
    ]
  }

  return props.isOneLocation || isRegionPage.value
    ? [ data ]
    : [
        {
          imgUrl: '/svg/calendar.svg',
          title: `Срок доставки`,
          link: {
            title: `Расписание рейсов ${locationText.value}`,
            to: {
              hash: '#run-schedule'
            }
          },
          text: [
            `Клиентам доступны межтерминальные перевозки ${itemsTerminalsText.value}. А также доставка "от двери до двери".`
          ]
        },
        {
          imgUrl: '/svg/pointer.svg',
          title: `Параметры маршрута ${locationText.value}`,
          text: [
            props.urlDirection?.distance ? `Расстояние: ${props.urlDirection?.distance} км` : '',
            `Срок транспортировки: ${props.refCalculator?.priceData?.delivery || 'Расчетное время'}`
          ]
        },
        data
      ]
})

function getTerminalTextEnd(count: number) {
  return `терминал${count > 1 ? 'а' : ''}`
}
</script>

<style lang="scss" scoped>
.locations-discount {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 60px;
  row-gap: 45px;
  align-items: flex-start;

  .locations-discount-info {
    text-align: justify;
  }

  .locations-discount-description {
    grid-area: 2/1/3/3;
  }

  .locations-discount-item {
    margin-bottom: 20px;
    align-items: flex-start;

    span {
      font-weight: bold;
    }

    a {
      display: block;
      text-decoration: underline;
    }
  }

  @media only screen and (max-width: $pub-sm) {
    gap: 20px;
  }

  @media only screen and (max-width: $pub-xs) {
    display: flex;
    flex-direction: column;

    &-banner {
      order: 3;
    }
  }
}
</style>
